<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCRMOrigin } from 'shared/helpers/env';
import GalleryView from '../../components/GalleryView.vue';
import type { IRefundIntentionCardMessage } from './types';

const { t } = useI18n();

const props = defineProps<{ message: IRefundIntentionCardMessage }>();
const { title, orderId, skuList: reverseItemList = [], returnReason, returnCredentials = [], labels, plan } = props.message ?? {};
const { coverImage, title: goodsName, specification, discountPriceText = '', count = '' } = reverseItemList[0];
const reason = returnReason ? `${returnReason.categoryName} - ${returnReason.reasonName}` : '';
const showViewMore = returnCredentials?.length > 3;
// const _returnCredentials = showViewMore ? returnCredentials?.slice(0, 3) : returnCredentials;

const showGallery = ref(false);
const currentAttachmentIndex = ref(0);

// 将 returnCredentials 转换为 GalleryView 需要的格式
const allAttachments = computed(() => {
  return returnCredentials.map((url, index) => ({
    file_type: 'image',
    data_url: url,
    message_id: `refund-credential-${index}`,
  }));
});

// 显示的图片列表（超过4张时只显示前3张，否则全部显示）
const displayedImages = computed(() => {
  return returnCredentials.length > 4 ? returnCredentials.slice(0, 3) : returnCredentials;
});

// 超出数量（超过4张时显示）
const remainingCount = computed(() => {
  return returnCredentials.length > 4 ? returnCredentials.length - 3 : 0;
});

const handleDetail = () => {
  window.open(`${getCRMOrigin()}/order-detail?orderId=${orderId}`);
};

const handleImageClick = (index: number) => {
  currentAttachmentIndex.value = index;
  showGallery.value = true;
};

const handleMoreClick = () => {
  currentAttachmentIndex.value = 0;
  showGallery.value = true;
};

const handleCloseGallery = () => {
  showGallery.value = false;
};
</script>

<template>
  <!-- 头 -->
  <div class="header">{{ title }}</div>
  <!-- 内容 -->
  <div class="body">
    <img class="bodyImg" :src="coverImage" />
    <div class="bodyContent">
      <div>
        <p class="bodyContentTitle">{{ goodsName }}</p>
        <div class="bodyContentSku">
          <div class="leading-[15px]">
            <img v-if="specification[0]?.pic" class="inline-block w-[10px] !h-[10px] rounded-[2px]" :src="specification[0]?.pic ?? ''">
            <span v-if="specification[0]?.pic && specification[1]?.value" class="font-bold text-[12px]"> / </span>
            <span v-if="specification[1]?.value" class="font-semibold text-[12px]">{{ specification[1]?.value }}</span>
          </div>
        </div>
      </div>
      <p class="bodyContentPriceCount">
        <span class="bodyContentCount">x{{ count }}</span>
        <span class="bodyContentPrice">{{ discountPriceText }}</span>
      </p>
    </div>
  </div>
  <div class="footer">
    <div v-if="reason" class="footerReason">{{ labels.returnReason }} {{ reason }}</div>
    <template v-if="returnCredentials.length > 0">
      <p class="returnCredentials">{{ labels.returnCredentials }} </p>
      <div class="returnCredentialsList">
        <img
          v-for="(item, index) in displayedImages"
          :key="item"
          class="returnCredentialsItem"
          :src="item"
          @click="handleImageClick(index)"
        />
        <div
          v-if="remainingCount > 0"
          class="returnCredentialsMore"
          @click="handleMoreClick"
        >
          +{{ remainingCount }}
        </div>
      </div>
    </template>
    <div v-if="plan?.title" class="footerPlan">{{ labels.returnPlan }} {{ plan.title }} {{ plan.note ? `(${plan.note})` : '' }}</div>
  </div>
  <!-- View 按钮 -->
  <div class="bg-[#000] text-[#fff] font-semibold text-[14px] text-center rounded-[10px] mt-[12px] h-[33px] leading-[33px] cursor-pointer" @click="handleDetail">
    {{ labels.view }}
  </div>
  <!-- GalleryView 全屏预览 -->
  <GalleryView
    v-if="showGallery && allAttachments.length > 0 && currentAttachmentIndex < allAttachments.length"
    v-model:show="showGallery"
    :attachment="allAttachments[currentAttachmentIndex]"
    :all-attachments="allAttachments"
    @close="handleCloseGallery"
  />
</template>

<style scoped>
.header {
  height: 28px;
  line-height: 28px;
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.body {
  margin-top: 10px;
  display: flex;
}

.bodyImg {
  width: 58px;
  height: 78px!important;
  margin-inline-end: 8px;
  flex-shrink: 0;
}

.bodyContent {
  flex: 1;
  text-align: start;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.bodyContentTitle {
  font-size: 12px;
  margin-bottom: 0;
  line-height: 15px;
}

.bodyContentSku {
  display: flex;
  align-items: center;
  gap: 2px;
  font-weight: 600;
  margin-bottom: 0;
  line-height: 15px;
  margin-top: 6px;
}

.bodyContentSkuItemPic {
  border-radius: 2px;
}

.bodyContentPriceCount {
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 18px;
  margin-bottom: 0;
}

.bodyContentPrice {
  font-weight: 700;
  font-size: 14px;
}

.bodyContentCount {
  font-size: 12px;
}

.bodyContentSkuSeparator {
  margin-inline-start: 2px;
  margin-inline-end: 2px;
}

.footer {
  margin-top: 10px;
  font-size: 12px;
  text-align: start;
}

.footerReason {
  font-size: 12px;
  margin-bottom: 0;
  line-height: 15px;
  white-space: pre-line;
  overflow: visible;
  text-overflow: unset;
}

.returnCredentials {
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  line-height: 15px;
}

.footerPlan {
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  line-height: 15px;
  white-space: wrap;
}

.returnCredentialsList {
  display: flex;
  margin-top: 10px;
  .returnCredentialsItem {
    width: 56px;
    height: 56px;
    aspect-ratio: 1 / 1!important;
    flex-shrink: 0;
    margin-right: 10px;
    cursor: pointer;
    overflow: hidden;
    object-fit: cover;
    border-radius: 4px;
  }
  .returnCredentialsMore {
    width: 56px;
    height: 56px;
    aspect-ratio: 1 / 1!important;
    flex-shrink: 0;
    margin-right: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #F4F4F4;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    color: #000;
  }
}

.viewMore {
  flex: 1;
  background-color: #F4F4F4;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  color: #000;
  font-size: 10px;
  font-weight: 400;

  .viewMoreText {
    margin-top: 4px;
    white-space: pre-line;
  }
}
</style>
